import { type Hex, mix, shade } from "@/color/oklch";
import { isPaletteKey, PALETTE_KEYS, type PaletteKey } from "./palette";
import { DEFAULT_STYLES, type SyntaxStyle, type Theme } from "./theme";
import {
  defaultExpr,
  type Expr,
  SYNTAX_KEYS,
  type SyntaxKey,
  TOKEN_MAP,
  TOKENS,
  type TokenId,
} from "./tokens";

export type ColorId = TokenId | `palette.${PaletteKey}`;

export interface Resolved {
  /** Every palette swatch (`palette.blue`) and every token, as hex. */
  colors: Record<ColorId, Hex>;
  styles: Record<SyntaxKey, SyntaxStyle>;
  /** Tokens whose override could not be used (cycle) and fell back to the default. */
  broken: TokenId[];
}

const FALLBACK: Hex = "#ff00ff";

export function resolveTheme(theme: Theme): Resolved {
  const colors = {} as Record<ColorId, Hex>;
  for (const key of PALETTE_KEYS) colors[`palette.${key}`] = theme.palette[key];

  const broken: TokenId[] = [];
  const visiting = new Set<TokenId>();

  const evalExpr = (expr: Expr): Hex => {
    if (typeof expr === "string") {
      if (expr.startsWith("$")) {
        const key = expr.slice(1);
        return isPaletteKey(key) ? theme.palette[key] : FALLBACK;
      }
      if (expr.startsWith("@")) return get(expr.slice(1) as TokenId);
      return expr as Hex;
    }
    if ("mix" in expr) return mix(evalExpr(expr.mix[0]), evalExpr(expr.mix[1]), expr.mix[2]);
    return shade(evalExpr(expr.shade[0]), expr.shade[1]);
  };

  function get(id: TokenId): Hex {
    const cached = colors[id];
    if (cached) return cached;
    const def = TOKEN_MAP.get(id);
    if (!def) return FALLBACK;
    if (visiting.has(id)) throw new CycleError(id);
    visiting.add(id);
    try {
      const override = theme.overrides[id];
      let value: Hex;
      if (override) {
        try {
          value = evalExpr(override);
        } catch (err) {
          if (!(err instanceof CycleError)) throw err;
          broken.push(id);
          value = evalExpr(defaultExpr(def, theme.variant));
        }
      } else {
        value = evalExpr(defaultExpr(def, theme.variant));
      }
      colors[id] = value;
      return value;
    } finally {
      visiting.delete(id);
    }
  }

  for (const def of TOKENS) {
    try {
      get(def.id);
    } catch (err) {
      if (!(err instanceof CycleError)) throw err;
      broken.push(def.id);
      colors[def.id] = FALLBACK;
    }
  }

  const styles = {} as Record<SyntaxKey, SyntaxStyle>;
  for (const key of SYNTAX_KEYS) styles[key] = theme.styles[key] ?? DEFAULT_STYLES[key] ?? {};

  return { colors, styles, broken: [...new Set(broken)] };
}

class CycleError extends Error {
  constructor(id: TokenId) {
    super(`reference cycle at ${id}`);
  }
}

/** Short human description of where a token's value comes from. */
export function describeExpr(expr: Expr): string {
  if (typeof expr === "string") return expr;
  if ("mix" in expr) {
    const [a, b, t] = expr.mix;
    return `mix(${describeExpr(a)}, ${describeExpr(b)}, ${Math.round(t * 100)}%)`;
  }
  const [a, dl] = expr.shade;
  return `${describeExpr(a)} ${dl >= 0 ? "+" : "−"}${Math.abs(Math.round(dl * 100))}L`;
}

/** The expression a token uses right now: its override, or the derived default. */
export function effectiveExpr(theme: Theme, id: TokenId): Expr | undefined {
  const def = TOKEN_MAP.get(id);
  if (!def) return undefined;
  return theme.overrides[id] ?? defaultExpr(def, theme.variant);
}

function mentions(expr: Expr, ref: string): boolean {
  if (typeof expr === "string") return expr === ref;
  if ("mix" in expr) return mentions(expr.mix[0], ref) || mentions(expr.mix[1], ref);
  return mentions(expr.shade[0], ref);
}

/** Tokens whose current expression refers directly to `$key` or `@token`. */
export function referencesTo(theme: Theme, ref: string): TokenId[] {
  return TOKENS.filter((t) => {
    const expr = effectiveExpr(theme, t.id);
    return expr !== undefined && mentions(expr, ref);
  }).map((t) => t.id);
}

/** Hex of a palette swatch or token (always defined after resolveTheme). */
export function hexOf(resolved: Resolved, id: ColorId): Hex {
  return resolved.colors[id] ?? FALLBACK;
}
